import React, { useEffect, useMemo, useState } from 'react';
import { ArrayHelper, IOptionType, MuiAutocompleteSelect, PillButton, ProjectDialog } from '@zz2/zz2-ui';
import { useAppDispatch, useAppSelector } from '../../../@types/redux';
import OptionType from '../../../@types/model/optionType';
import { Button } from '@mui/material';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import { IApiError } from '../../../@types/other';
import StockHttpService from '../../../services/http/stock/stockHttpService';
import { dataSetStockDataViews } from '../../../store/data/Actions';
import { ISendStockToStorageUnit } from '../../../@types/model/stock/sendStockToStorageUnit';

interface ISendToStorageUnitDialogProps {
    isOpen : boolean;
    selectedStockIds : Array<number>;
    isLoading : boolean;
    setIsLoading : (value : boolean) => void;
    onClose : () => void;
}

const SendToStorageUnitDialog = (props : ISendToStorageUnitDialogProps) => {
    const dispatch = useAppDispatch();
    const stocks = useAppSelector(state => state.data.stockDataViews);
    const storageUnits = useAppSelector(state => state.masterData.storageUnits);
    const storageLocations = useAppSelector(state => state.masterData.storageLocations);

    const [selectedStorageUnit, setSelectedStorageUnit] = useState<IOptionType | null>(null);
    const [selectedStorageLocation, setSelectedStorageLocation] = useState<IOptionType | null>(null);

    /*================================================================================================================
     *                                                  Queries
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const sendStockToStorageUnit = async () => {
        if (props.selectedStockIds.length < 1 || !selectedStorageUnit) {
            generalShowErrorSnackbar('Selected stock or storage unit not found');
        }

        try {
            const stockIds = [...props.selectedStockIds];
            const newStorageUnitId = Number(selectedStorageUnit?.value ?? 0);
            const newStorageLocationId = Number(selectedStorageLocation?.value ?? 0);

            props.setIsLoading(true);

            const upsert : ISendStockToStorageUnit = {
                stockIds,
                newStorageUnitId,
                newStorageLocationId
            };

            const res = await StockHttpService.sendStocksToStorageUnit(upsert);

            if (res && res.data) {
                const newStocks = res.data;

                let stockViews = [...stocks];
                stockViews.forEach((stock) => {
                    const newStock = newStocks.find(x => x.id === stock.id);
                    if (newStock) {
                        stock.currentStorageUnitId = newStorageUnitId;
                        stock.currentStorageLocationId = newStorageLocationId;
                        stock.updatedByName = newStock?.updatedByName;
                        stock.updatedOn = newStock?.updatedOn;
                    }
                    stockViews = ArrayHelper.upsertElement(stockViews, stock, x => x.id === stock.id) ?? [];
                });
                dispatch(dataSetStockDataViews(stockViews));
                generalShowSuccessSnackbar('Stock moved successfully.');
            } else {
                generalShowErrorSnackbar('Selected stock or storage unit not found');
            }
        } catch (e) {
            const error = e as IApiError;
            if (error?.status === 400) {
                generalShowErrorSnackbar(error.data?.Message ?? '');
            } else {
                generalShowErrorSnackbar('An error occurred moving the stock to a storage unit.');
            }
        } finally {
            props.onClose();
            props.setIsLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const onClose = () => {
        setSelectedStorageUnit(null);
        setSelectedStorageLocation(null);
        props.onClose();
    };

    const onStorageUnitChange = (selectedOption : IOptionType | null) : void => {
        setSelectedStorageUnit(selectedOption);
        setSelectedStorageLocation(null);
    };

    const onStorageLocationChange = (selectedOption : IOptionType | null) : void => {
        setSelectedStorageLocation(selectedOption);
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const getStorageUnitOptions = useMemo(() => {
        return storageUnits.filter(x => x.isActive).map(OptionType.fromDataModel);
    }, [storageUnits]);

    const getStorageLocationOptions = useMemo(() => {
        return storageLocations
            .filter(x => x.isActive
                && x.storageUnitLocations.some(y => y.isActive && y.storageUnitId === selectedStorageUnit?.value))
            .map(OptionType.fromDataModel);
    }, [storageLocations, selectedStorageUnit]);

    const stockBarcodes = useMemo(() => {
        return props.selectedStockIds.map(x => <div>{stocks.find(y => y.id === x)?.barcode}</div>);
    }, [props.selectedStockIds, stocks]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <ProjectDialog
            title={'Send to Storage Unit'}
            maxWidth={'xs'}
            isLoadingCircular={props.isLoading}
            isOpen={props.isOpen}
            onClose={props.onClose}
            fullWidth
        >
            <div className={'fdc hfill wfill p10'}>
                <MuiAutocompleteSelect
                    label={'Storage Unit'}
                    name={'storageUnit'}
                    className={'wfill'}
                    options={getStorageUnitOptions}
                    value={selectedStorageUnit}
                    onChange={(value) => onStorageUnitChange(value as IOptionType | null)}
                    disabled={props.isLoading}
                />
                <MuiAutocompleteSelect
                    label={'Storage Location (Optional)'}
                    name={'storageLocation'}
                    className={'wfill'}
                    options={getStorageLocationOptions}
                    value={selectedStorageLocation}
                    onChange={(value) => onStorageLocationChange(value as IOptionType | null)}
                    disabled={props.isLoading || !selectedStorageUnit}
                />
                <div className='fwb pt10'>Selected Stock:</div>
                <div className='fdc pt10 pb10'>
                    {stockBarcodes}
                </div>
                <div className={'fdr pt10 pb10 wfill jcfe'}>
                    <Button
                        className={'fwb h35'}
                        variant={'text'}
                        color={'primary'}
                        onClick={onClose}
                    >
                            Cancel
                    </Button>
                    <PillButton
                        disabled={props.isLoading || !selectedStorageUnit}
                        className={'ml15 pl20 zi0 pr20 h35'}
                        text={'Submit'}
                        color={'secondary'}
                        onClick={sendStockToStorageUnit}
                    />
                </div>
            </div>
        </ProjectDialog>
    );
}

export default SendToStorageUnitDialog;